import { Amplify } from 'aws-amplify'
import { fetchAuthSession, signOut as amplifySignOut, getCurrentUser as amplifyGetCurrentUser } from 'aws-amplify/auth'
import type { JWT } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import { userLogout } from '@redux/actions/session'
import { SESSIONS_URL, SSO_URL } from '@utils/api-url'
import { resetCognitoClient } from '@utils/aws-cognitoIdentity'
import { PORTALS_VAR, PAGE } from '@utils/constants'
import { isGodMode } from '@utils/helpers'
import { getStorageAdapter } from '@shell/adapters/storage'
import type { AuthContract, AuthEvent, LoginAsParams, AuthCredentials } from '@shell/contracts/auth.contract'
import type { CognitoUserProps, IPayload } from '@interfaces/StoreState'
import * as queryString from 'query-string'

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * God-mode logging for admin token debugging.
 * Preserved from the original aws-usersession.ts for feature parity.
 */
const godModeLog = (idToken: JWT, parentLog: string, log: string) => {
  const cognitoGroups: string[] = (idToken.payload['cognito:groups'] as string[]) ?? []
  const isAdmin = cognitoGroups.some((group: string) => PAGE.ROLE_ADMINS.includes(group))
  const godMode = isGodMode(isAdmin) ?? false

  if (godMode) {
    const exp = idToken.payload.exp
    console.log('godMode getSession', {
      expiration: exp ? new Date(exp * 1000).toISOString() : 'unknown',
      logMessage: `${parentLog} - ${log}`,
    })
  }
}

// ---------------------------------------------------------------------------
// AmplifyAuthAdapter — implements AuthContract using Amplify v6
// ---------------------------------------------------------------------------

class AmplifyAuthAdapter implements AuthContract {
  private _configured = false

  configureAuth(stateParam?: string | null): void {
    if (this._configured) return

    let qs = ''
    if (stateParam) {
      qs += `?${stateParam}`
    }

    const redirectSignIn =
      typeof window !== 'undefined' ? `${window.location.origin}${qs}` : `http://localhost:3000${qs}`

    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: process.env.AWS_USER_POOLS_ID!,
          userPoolClientId: process.env.AWS_USER_POOLS_WEB_CLIENT_ID!,
          identityPoolId: process.env.AWS_COGNITO_IDENTITY_POOL_ID!,
          loginWith: {
            oauth: {
              domain: process.env.OAUTH_DOMAIN!,
              scopes: ['phone', 'email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
              redirectSignIn: [redirectSignIn],
              redirectSignOut: ['/'],
              responseType: 'token',
            },
          },
        },
      },
      Storage: {
        S3: {
          bucket: process.env.AWS_S3_BUCKET!,
          region: process.env.AWS_S3_REGION!,
        },
      },
    })

    this._configured = true

    // Amplify v6's OAuth listener requires signInWithRedirect to set an "inflight" flag.
    // Since the portal uses an external SSO that redirects with tokens in the URL hash
    // (implicit grant flow), we must cache the tokens manually so fetchAuthSession() works.
    //
    // When hash tokens are present, _cacheHashTokens dispatches 'signIn' directly
    // (skipping 'configured') to avoid double-calling getAwsUserData in AwsAuth.
    // When no hash tokens exist, we dispatch 'configured' so AwsAuth calls checkUser().
    if (typeof window !== 'undefined' && this._cacheHashTokens()) {
      // Tokens found and cached — 'signIn' already dispatched inside _cacheHashTokens
      return
    }

    // No hash tokens — dispatch 'configured' so AwsAuth's checkUser() runs
    // (Amplify v6 does NOT dispatch this automatically like v5 did)
    Hub.dispatch('auth', { event: 'configured' })
  }

  /**
   * Parse OAuth tokens from URL hash (implicit grant) and store them in localStorage
   * in the format Amplify v6's DefaultTokenStore expects, so fetchAuthSession() works.
   *
   * Returns true if tokens were found and cached, false otherwise.
   */
  private _cacheHashTokens(): boolean {
    const hash = window.location.hash
    if (!hash || !hash.includes('access_token')) return false

    const params = new URLSearchParams(hash.substring(1))
    const accessToken = params.get('access_token')
    const idToken = params.get('id_token')
    const refreshToken = params.get('refresh_token')

    if (!accessToken) return false

    // Decode JWT to get username
    let username = 'username'
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]))
      username = payload.username || payload['cognito:username'] || payload.sub || 'username'
    } catch {
      // fallback to default
    }

    const clientId = process.env.AWS_USER_POOLS_WEB_CLIENT_ID!
    const prefix = `CognitoIdentityServiceProvider.${clientId}`

    // Store in the exact format v6's DefaultTokenStore reads
    localStorage.setItem(`${prefix}.LastAuthUser`, username)
    localStorage.setItem(`${prefix}.${username}.accessToken`, accessToken)
    if (idToken) {
      localStorage.setItem(`${prefix}.${username}.idToken`, idToken)
    }
    if (refreshToken) {
      localStorage.setItem(`${prefix}.${username}.refreshToken`, refreshToken)
    }
    localStorage.setItem(`${prefix}.${username}.clockDrift`, '0')

    // [P1-3 fix] Clear tokens from URL hash to prevent:
    // - Tokens visible in browser history / screenshots / telemetry
    // - Re-caching and re-dispatching signIn on every page reload
    window.history.replaceState(null, '', window.location.pathname + window.location.search)

    // Dispatch signIn so AwsAuth picks up the auth flow (only event, no 'configured')
    Hub.dispatch('auth', { event: 'signIn' })
    return true
  }

  async getIdToken(): Promise<string> {
    const session = await fetchAuthSession({ forceRefresh: false })
    const token = session.tokens?.idToken

    if (!token) {
      throw new Error('No ID token available — user is not authenticated')
    }

    godModeLog(token, 'authAdapter.getIdToken', 'fetchAuthSession')
    return token.toString()
  }

  async getAccessToken(): Promise<string> {
    const session = await fetchAuthSession({ forceRefresh: false })
    const token = session.tokens?.accessToken

    if (!token) {
      throw new Error('No access token available — user is not authenticated')
    }

    return token.toString()
  }

  async getCredentials(): Promise<AuthCredentials> {
    const session = await fetchAuthSession({ forceRefresh: false })
    const credentials = session.credentials

    if (!credentials) {
      throw new Error('No credentials available — user is not authenticated')
    }

    return {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken ?? '',
      identityId: session.identityId,
      authenticated: true,
    }
  }

  async getCurrentUser(): Promise<CognitoUserProps> {
    const { username } = await amplifyGetCurrentUser()
    const session = await fetchAuthSession({ forceRefresh: false })
    const idToken = session.tokens?.idToken
    const payload = idToken?.payload

    // Reconstruct the attributes from the token payload.
    // In v6, fetchUserAttributes() is the official way, but the token payload
    // has everything we need and avoids an extra API call.
    const attributes: Record<string, string | boolean | undefined> = {}
    if (payload) {
      const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined)
      attributes.email = str(payload.email)
      attributes.locale = str(payload.locale)
      attributes['custom:customerId'] = str(payload['custom:customerId'])
      attributes['custom:federatedExternalId'] = str(payload['custom:federatedExternalId'])
      attributes.given_name = str(payload.given_name)
      attributes.family_name = str(payload.family_name)
      attributes.name = str(payload.name)
      attributes.zoneinfo = str(payload.zoneinfo)
      attributes.sub = str(payload.sub)
      if (typeof payload.email_verified === 'boolean') {
        attributes.email_verified = payload.email_verified
      }
    }

    // Map v6 JwtPayload → IPayload expected by CognitoUserProps.signInUserSession
    const idPayload: IPayload | undefined = payload
      ? {
          sub: payload.sub as string | undefined,
          'cognito:groups': payload['cognito:groups'] as string[] | undefined,
          email_verified: payload.email_verified as boolean | undefined,
          iss: payload.iss,
          'cognito:username': payload['cognito:username'] as string | undefined,
          given_name: payload.given_name as string | undefined,
          locale: payload.locale as string | undefined,
          aud: typeof payload.aud === 'string' ? payload.aud : payload.aud?.[0],
          'custom:customerId': payload['custom:customerId'] as string | undefined,
          token_use: payload.token_use as string | undefined,
          auth_time: payload.auth_time as number | undefined,
          name: payload.name as string | undefined,
          exp: payload.exp,
          iat: payload.iat,
        }
      : undefined

    return {
      username,
      attributes,
      signInUserSession: {
        idToken: {
          jwtToken: idToken?.toString(),
          payload: idPayload,
        },
      },
    }
  }

  async signOut(): Promise<void> {
    try {
      // Call the backend logout endpoint before clearing tokens
      const idToken = await this.getIdToken()
      const sessionBearer = `Bearer ${idToken}`
      await fetch(`${SESSIONS_URL}/logout${process.env.ENV_NAME === 'staging' ? '?env=sit' : ''}`, {
        headers: { Authorization: sessionBearer },
      })
    } catch (e) {
      // catch so user can logout normally even if token is expired
    }

    userLogout()

    // Clear cached Cognito admin client so stale credentials are never reused
    resetCognitoClient()

    // Use StorageContract.clearAuth() instead of localStorage.clear()
    // This preserves UI preferences (portal selections, autoplay, etc.)
    getStorageAdapter().clearAuth()

    // Don't call amplifySignOut() — it tries to redirect to Cognito's logout
    // endpoint internally, which races with our own redirect below.
    // Instead, we redirect directly to Cognito's /logout endpoint with the
    // required parameters to fully invalidate the SSO session cookie.
    const clientId = process.env.AWS_USER_POOLS_WEB_CLIENT_ID!
    const logoutUri = encodeURIComponent(`${SSO_URL}signout`)
    window.location.href = `https://${process.env.OAUTH_DOMAIN}/logout?client_id=${clientId}&logout_uri=${logoutUri}`
  }

  onAuthEvent(callback: (event: AuthEvent) => void): () => void {
    // Amplify v6 renamed some Hub auth events. Map them back to the v5 names
    // that AwsAuth/index.tsx expects.
    const v6ToV5Event: Record<string, AuthEvent> = {
      signedIn: 'signIn',
      signedOut: 'signOut',
      signIn_failure: 'signIn_failure',
      tokenRefresh: 'tokenRefresh',
      tokenRefresh_failure: 'signIn_failure',
      configured: 'configured',
      parsingCallbackUrl: 'parsingCallbackUrl',
    }

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      const rawEvent: string = payload.event
      const mapped = v6ToV5Event[rawEvent] ?? rawEvent
      callback(mapped as AuthEvent)
    })

    return unsubscribe
  }

  async getCognitoGroups(): Promise<string[]> {
    const session = await fetchAuthSession({ forceRefresh: false })
    const payload = session.tokens?.idToken?.payload
    return (payload?.['cognito:groups'] as string[]) ?? []
  }

  getLoginAs(): LoginAsParams {
    if (typeof window === 'undefined') return {}

    const { aas, aai } = queryString.parse(window.location.search)
    let sfID: LoginAsParams = { aas, aai }
    if (!sfID.aas && !sfID.aai) {
      sfID = queryString.parse(getStorageAdapter().get(PORTALS_VAR.LOCAL_SFID) ?? '')
    }
    return sfID
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

let instance: AuthContract | null = null

export const getAuthAdapter = (): AuthContract => {
  if (!instance) {
    instance = new AmplifyAuthAdapter()
  }
  return instance
}
